import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IStatsList } from '@app/teachers/teacher-card/teacher-card-variables';
import {
  AccountService,
  AuthService,
  BillingService,
  FilesService,
  PlatformService,
  SchoolsService,
} from '@core/services';
import { WebsocketService } from '@core/websocket';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { loadMe } from '@store/actions/profile.actions';
import { getMe, getUserLanguages } from '@store/reducers/profile.reducer';
import { addHours, format } from 'date-fns';
import { PresetTypeEnum } from 'lingo2-forms';
import { Router as ContentRouter, SchoolStats } from 'lingo2-models';
import {
  BillingMeetingChargingEnum,
  CSchool,
  CurrencyEnum,
  EContentPanelRoute,
  ESchoolParticipantRole,
  ESchoolStatus,
  FilePurposeEnum,
  ImageSizeEnum,
  Meeting,
  MeetingAccessEnum,
  MeetingBeginEnum,
  MeetingTypeEnum,
  Subject,
  User,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonState, OnUiCover } from 'onclass-ui';
import { of, Subject as RxJSSubject } from 'rxjs';
import { filter, mergeAll, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-school-header',
  templateUrl: './school-header.component.html',
  styleUrls: ['./school-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchoolHeaderComponent extends ChangableComponent implements OnInit {
  @Input()
  public set school(data: CSchool) {
    if (data) {
      this._school = data;
      this.init();
    }
  }

  public get school(): CSchool {
    return this._school;
  }

  @Input() subjectId: number;
  @Input() isModal: boolean;
  @Output() onSubjectChange = new EventEmitter<number>();
  @Output() favoriteChanged = new EventEmitter<boolean>();
  public schoolForm = false;
  public meetingForm = false;
  public headCover: OnUiCover;
  public title: string;
  public shortDescription: string;
  public description: string;
  public addParticipantActionState: OnUiButtonState = 'default';
  public leaveSchoolState: OnUiButtonState = 'default';
  public newMeeting: Meeting;
  public preset: PresetTypeEnum = PresetTypeEnum.quick;
  public stats: Array<Partial<IStatsList>> = [];
  public creditsAmount: number;
  public cover: OnUiCover;
  public filePurpose = FilePurposeEnum.content;
  public openAlertLeaveSchoolState = false;
  public openActionsPopup = false;
  public subjects: Subject[] = [];
  public schoolUrl: string;
  public fullSchoolUrl: string;
  public settingsUrl: string;
  public copied = false;
  public alertOpened = false;
  public openComplainForm = false;
  public hasVerifiedSchool: boolean;
  public meetingRoute = ContentRouter.meetingRoute;
  private _school: CSchool;
  private _me: User;
  private _language: string;
  private loadCreditsSubject = this.register(new RxJSSubject<boolean>());

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    private schoolService: SchoolsService,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private billingService: BillingService,
    private filesService: FilesService,
    private authService: AuthService,
    private websocket: WebsocketService,
    protected cdr: ChangeDetectorRef,
    protected platform: PlatformService,
  ) {
    super(cdr, platform);
    this.newMeeting = new Meeting();
  }

  public ngOnInit(): void {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMe());
        },
      });

    this.store
      .select(getUserLanguages)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this._language = res[0];
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    of(...[this.loadCredits$, this.watchWsBalanceUpdate$, this.watchWsReconnected$])
      .pipe(mergeAll(), takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.loadCredits();
  }

  private init() {
    this.prepareHeader();
    this.prepareStats();
    const currentDate = new Date();
    this.newMeeting = new Meeting({
      title: `${this.school.title} - ${format(new Date(), 'MM-dd-yyyy')}`,
      type: MeetingTypeEnum.group,
      category_id: 1,
      access: MeetingAccessEnum.public,
      charging: BillingMeetingChargingEnum.fee,
      begin: MeetingBeginEnum.specified_time,
      begin_at: addHours(new Date(format(new Date(), 'MM/dd/yyyy')), currentDate.getHours()),
      end_at: addHours(new Date(format(new Date(), 'MM/dd/yyyy')), currentDate.getHours() + 1),
      school_id: this.school?.id,
      school: this.school,
    });
  }

  public get isTeacher(): boolean {
    return AccountService.isAsIfTeacher(this._me);
  }

  public getSubjectTitle(subject: Subject) {
    if (this._language === 'ru') {
      return subject['title_ru'];
    } else {
      return subject['title_en'];
    }
  }

  public onToggleForm() {
    this.schoolForm = !this.schoolForm;
    this.detectChanges();
  }

  public changed(event: boolean) {
    if (event) {
      this.loadSchool();
      this.onToggleForm();
    }
  }

  public onToggleMeetingForm() {
    this.meetingForm = !this.meetingForm;
    this.detectChanges();
  }

  public onAddParticipant() {
    if (!this.authService.isAuthenticated) {
      this.authService.showAuthModal(() => {
        this.detectChanges();
      });
    } else {
      this.addParticipantActionState = 'progress';
      this.schoolService
        .createParticipant(this._school.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.addParticipantActionState = 'done';
            this.loadSchool();
            this.detectChanges();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
            this.addParticipantActionState = 'fail';
            this.detectChanges();
          },
        });

      this.setTimeout(() => {
        this.addParticipantActionState = 'default';
        this.detectChanges();
      }, 2000);
    }
  }

  public onNavigateToSubject(subject: Subject): void {
    this.onSubjectChange.emit(subject.id);
    this.detectChanges();
  }

  public get meIsOwnerSchool(): boolean {
    return !!this._school?.participants
      .filter((_p) => _p.role === ESchoolParticipantRole.owner)
      .find((_part) => _part.account_id === this._me?.id);
  }

  public get meIsTeacherSchool(): boolean {
    return !!this._school?.participants
      .filter((_p) => _p.role === ESchoolParticipantRole.teacher)
      .find((_part) => _part.account_id === this._me?.id);
  }

  public get meIsStudentSchool(): boolean {
    return !!this._school?.participants
      .filter((_p) => _p.role === ESchoolParticipantRole.student)
      .find((_part) => _part.account_id === this._me?.id);
  }

  public get hasTeachersInSchool(): boolean {
    return !!this._school?.participants.filter((_p) => _p.role === ESchoolParticipantRole.teacher);
  }

  public onToggleAlertLeaveSchool() {
    this.openAlertLeaveSchoolState = !this.openAlertLeaveSchoolState;
    this.detectChanges();
  }

  public onOpenActionsPopup() {
    this.openActionsPopup = !this.openActionsPopup;
    this.detectChanges();
  }

  public onLeaveSchool() {
    const meParticipantId = this._school.participants.find((_p) => _p.account_id === this._me?.id).id;
    this.leaveSchoolState = 'progress';
    this.detectChanges();

    this.schoolService
      .removeParticipant(meParticipantId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (v) => {
          this.leaveSchoolState = 'done';
          this.loadSchool();
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.leaveSchoolState = 'fail';
          this.markForCheck();
        },
      });

    this.setTimeout(() => {
      this.leaveSchoolState = 'default';
      this.onToggleAlertLeaveSchool();
      this.detectChanges();
    }, 1000);
  }

  public subjectsTrackByFn(index, subject: Subject): number {
    return subject.id;
  }

  /** Credits */

  private loadCredits() {
    this.loadCreditsSubject.next(true);
  }

  private get loadCredits$() {
    return this.loadCreditsSubject.pipe(
      switchMap(() => this.billingService.getAvailableAmount(CurrencyEnum.credits)),
      tap((amount: number) => {
        this.creditsAmount = amount;
        this.detectChanges();
      }),
    );
  }

  private get watchWsBalanceUpdate$() {
    return this.websocket.onBalanceUpdate.pipe(tap(() => this.loadCredits()));
  }

  private get watchWsReconnected$() {
    return this.websocket.status$.pipe(
      filter((isOnline) => isOnline),
      tap(() => this.loadCredits()),
    );
  }

  /** */

  private prepareHeader() {
    this.subjects = this.school?.subjects;
    this.headCover = {
      img: FilesService.getFileUrlBySize(this._school.head_cover_id, ImageSizeEnum.wide),
    };
    this.cover = {
      img: FilesService.getFileUrlBySize(this._school.cover_id, ImageSizeEnum.wide),
      form: 'circle',
    };
    this.schoolUrl = SchoolsService.schoolRoute(this._school, false).join('/');
    this.fullSchoolUrl = SchoolsService.schoolRoute(this._school).join('/');
    this.settingsUrl = this.schoolUrl + '/settings';
    this.title = this._school?.title;
    this.shortDescription = this._school?.short_description;
    this.description = this._school?.description;
    this.hasVerifiedSchool = this._school?.status >= ESchoolStatus.verified;
  }

  private loadSchool() {
    this.schoolService
      .getSchoolByIdOrSlug(this._school.slug, undefined, 'LOAD_TIME')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (school) => {
          this._school = school;
          this.prepareHeader();
          this.prepareStats();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onFavoriteChanged() {
    this.favoriteChanged.emit(true);
  }

  public navigateToSchoolPage() {
    this.router.navigate([this.schoolUrl]);
  }

  public onCopied() {
    this.copied = true;
    this.detectChanges();

    this.setTimeout(() => {
      this.copied = false;
      this.detectChanges();
    }, 1000);
  }

  public onToggleAlert() {
    this.alertOpened = !this.alertOpened;
    this.detectChanges();
  }

  public onToggleComplainForm() {
    this.openComplainForm = !this.openComplainForm;
  }

  public onRemoveSchool() {
    if (!this.meIsOwnerSchool) {
      return;
    }
    this.schoolService
      .removeSchool(this._school.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.onToggleAlert();
          this.schoolForm = false;
          this.router.navigate([EContentPanelRoute.schools]);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.onToggleAlert();
          this.schoolForm = false;
        },
      });
  }

  private prepareStats() {
    const stats: Partial<SchoolStats> = this._school ? this._school.stats || {} : {};

    this.stats = [
      {
        name: 'classes_count',
        title: 'teacher-card.stats.classes',
        value: stats?.meeting_count || 0,
      },
      {
        name: 'students_count',
        title: 'teacher-card.stats.students',
        value: stats?.participant_count || 0,
      },
    ];
  }

  public trackByFn(index) {
    return index;
  }
}
